import { getRendererConfig } from '@src/config/rendererConfig';
import { useAssetPrewarmingService } from '@src/services/ServiceContainerContext';
import { WarmedRenderer } from '@src/services/WarmedRenderer';
import { LogManager } from '@src/utility/Logger';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { extractErrorInfo } from '../../utility/error';

const logger = LogManager.getLogger('component:PrewarmedRenderer');

/**
 * API surface for consumers to claim/release the pre-warmed Creatomate
 * Renderer instance. The iframe lives permanently in the provider's DOM
 * tree and is visually repositioned via CSS `position: fixed` — it is
 * never reparented (browsers fully reload iframes on DOM reparenting).
 */
export type PrewarmedRendererAPI = {
  /**
   * Claim the pre-warmed renderer. Returns the Renderer instance + its
   * container div, or null if the renderer hasn't finished loading yet.
   *
   * Ownership is lax: subsequent calls return the same instance. We NEVER
   * re-load the iframe, always reuse, NEVER dispose (until the provider
   * unmounts with ContentStudio).
   */
  claim(): { renderer: WarmedRenderer; container: HTMLDivElement } | null;

  /**
   * Release the visual claim. Pauses the renderer (to stop audio playing
   * offscreen) and moves the iframe container back to its offscreen position.
   * Does NOT dispose or reset the renderer — it stays warm for the next claim.
   */
  unclaim(): void;

  /** Whether the pre-warmed renderer has completed its onReady handshake. */
  isReady: boolean;

  /**
   * Position the iframe container to visually overlay a target element.
   * Called by the consumer to "dock" the iframe into the modal's preview area.
   * Uses position: fixed with the target's bounding client rect.
   *
   * @param iframeStyle — Optional styles applied to the iframe child inside the
   *   host. Useful for oversizing the iframe to clip vendor margins (e.g.
   *   Creatomate's interactive-mode off-canvas margin).
   */
  positionOver(
    targetRect: DOMRect,
    style?: React.CSSProperties,
    iframeStyle?: React.CSSProperties,
  ): void;

  /**
   * Merge ad-hoc styles onto the iframe host. Use to hide the docked iframe
   * during source swaps (e.g. aspect-ratio toggle) so stale renderer content
   * doesn't peek through while the new source is loading. The host's other
   * inline positioning is preserved.
   */
  setHostStyle(style: React.CSSProperties): void;
};

const PrewarmedRendererContext = createContext<PrewarmedRendererAPI | null>(
  null,
);

export function usePrewarmedRenderer(): PrewarmedRendererAPI | null {
  return useContext(PrewarmedRendererContext);
}

/**
 * Offscreen styles — park the iframe host outside the viewport so it
 * occupies negligible layout space and is invisible to the user.
 */
const OFFSCREEN_STYLE: React.CSSProperties = {
  position: 'fixed',
  left: '-9999px',
  top: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  pointerEvents: 'none',
};

/**
 * Provider that pre-warms a single Creatomate Renderer iframe when
 * ContentStudio mounts. The iframe is created once and reused across
 * every clip-preview modal open/close cycle for the lifetime of the
 * Content Studio page.
 *
 * Mount this component once at the ContentStudio level, wrapping the
 * subtree that contains the clip preview modal hierarchy.
 */
export const PrewarmedRendererProvider: React.FC<{
  children: React.ReactNode;
  /** Video URL to pre-cache into the warmer renderer. Computed by the parent. */
  videoCacheUrl?: string | null;
}> = ({ children, videoCacheUrl }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<WarmedRenderer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const assetPrewarmingService = useAssetPrewarmingService();

  // Boot the WarmedRenderer on mount, dispose on unmount. `dispose()`
  // override evicts our per-renderer cache tracking automatically.
  useEffect(() => {
    const hostDiv = hostRef.current;
    if (!hostDiv) return;

    logger.info('Pre-warming Creatomate iframe');

    const rendererConfig = getRendererConfig(true);
    const renderer = new WarmedRenderer(
      hostDiv,
      'interactive',
      rendererConfig.rendererId,
      assetPrewarmingService,
    );
    rendererRef.current = renderer;

    renderer.onReady = async () => {
      // Configure renderer defaults once — avoids repeating on every claim.
      try {
        await renderer.setZoom('auto');
        await renderer.setLoop(false);
        await renderer.setCacheBypassRules([]);
        await renderer.setControls(false);
      } catch (err) {
        logger.error(
          'Error configuring pre-warmed renderer defaults',
          extractErrorInfo(err),
        );
      }
      logger.info('Pre-warmed renderer is ready');
      setIsReady(true);
    };

    return () => {
      logger.info('Disposing pre-warmed renderer');
      renderer.dispose();
      rendererRef.current = null;
      setIsReady(false);
    };
  }, [assetPrewarmingService]);

  // Pre-cache the video blob once the renderer is ready. If videoCacheUrl
  // changes (e.g. story loads after mount), this fires again.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!isReady || !renderer || !videoCacheUrl) return;

    logger.info('Pre-caching video asset in warmer renderer', {
      videoCacheUrl,
    });

    renderer.warmAsset(videoCacheUrl).catch((err: unknown) => {
      logger.warn('Failed to pre-cache video in warmer renderer', {
        error: err,
        videoCacheUrl,
      });
    });
  }, [isReady, videoCacheUrl]);

  // ---- API methods (stable refs via useCallback) ----

  const claim = useCallback((): {
    renderer: WarmedRenderer;
    container: HTMLDivElement;
  } | null => {
    const renderer = rendererRef.current;
    const container = hostRef.current;
    if (!renderer || !container || !renderer.ready) {
      logger.debug('claim() — renderer not ready yet, returning null');
      return null;
    }
    logger.info('Pre-warmed renderer claimed');
    return { renderer, container };
  }, []);

  const unclaim = useCallback((): void => {
    const renderer = rendererRef.current;
    const container = hostRef.current;

    // Reset so the next claim starts clean.
    if (renderer?.ready) {
      try {
        renderer.pause();
        renderer.setActiveElements([]);
        renderer.setActiveComposition(null);
        renderer.setSource({ elements: [] });
      } catch {
        // Renderer may already be in a disposed or errored state.
      }
    }

    // Move the host div back offscreen. Reset opacity/transition so a
    // mid-swap unmount (where the host was hidden via setHostStyle) doesn't
    // leave the next claim with a transparent iframe.
    if (container) {
      Object.assign(container.style, OFFSCREEN_STYLE, {
        zIndex: '',
        opacity: '',
        transition: '',
      });
    }

    logger.info('Pre-warmed renderer unclaimed — moved offscreen');
  }, []);

  const positionOver = useCallback(
    (
      rect: DOMRect,
      style?: React.CSSProperties,
      iframeStyle?: React.CSSProperties,
    ): void => {
      const container = hostRef.current;
      if (!container) return;

      Object.assign(container.style, {
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        overflow: 'hidden',
        pointerEvents: 'auto',
        zIndex: '1000006',
        ...style,
      });

      if (iframeStyle) {
        const iframe = container.querySelector('iframe');
        if (iframe) {
          Object.assign(iframe.style, iframeStyle);
        }
      }
    },
    [],
  );

  const setHostStyle = useCallback((style: React.CSSProperties): void => {
    const container = hostRef.current;
    if (!container) return;
    Object.assign(container.style, style);
  }, []);

  // Build a stable context value — the callbacks are already stable via
  // useCallback, so we only need to rebuild when isReady changes.
  const api = React.useMemo<PrewarmedRendererAPI>(
    () => ({ claim, unclaim, isReady, positionOver, setHostStyle }),
    [claim, unclaim, isReady, positionOver, setHostStyle],
  );

  return (
    <PrewarmedRendererContext.Provider value={api}>
      {/* The offscreen host div — the iframe lives here permanently. */}
      <div ref={hostRef} style={OFFSCREEN_STYLE} />
      {children}
    </PrewarmedRendererContext.Provider>
  );
};

export default PrewarmedRendererContext;
